// Pulls real incident history off GitHub's Statuspage and turns it into
// "hours GitHub has been down since March 2, 2026".
//
// Two sources, because neither one alone covers the window:
//   - /api/v2/incidents.json  -> the 50 most recent incidents with exact ISO
//                                timestamps (and the only place an *ongoing*
//                                incident shows up as unresolved).
//   - /history.json?page=N    -> three months of incidents per page, but the
//                                times are baked into a display string.
// Both send `access-control-allow-origin: *`, so the browser can hit them directly.

export const SINCE = Date.UTC(2026, 2, 2, 0, 0, 0); // March 2, 2026 00:00 UTC

const STATUS_PAGE = 'https://www.githubstatus.com';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "Aug 26, 15:09 - 16:07"  |  "Apr 30, 23:10 - May 1, 01:40"  |  "Aug 26, 15:09" (ongoing)
const TIMESTAMP_RE = /^(\w{3}) (\d{1,2}), (\d{1,2}):(\d{2})(?:\s*-\s*(?:(\w{3}) (\d{1,2}), )?(\d{1,2}):(\d{2}))?$/;

function parseHistoryTimestamp(raw, year) {
  const text = raw.replace(/<[^>]*>/g, '').replace(/UTC/i, '').replace(/\s+/g, ' ').trim();
  const m = TIMESTAMP_RE.exec(text);
  if (!m) return null;

  const [, startMon, startDay, startHour, startMin, endMon, endDay, endHour, endMin] = m;
  const startMonth = MONTHS.indexOf(startMon);
  if (startMonth === -1) return null;

  const start = Date.UTC(year, startMonth, +startDay, +startHour, +startMin);
  if (endHour === undefined) return { start, end: null, ongoing: true };

  let end;
  if (endMon) {
    const endMonth = MONTHS.indexOf(endMon);
    if (endMonth === -1) return null;
    // A December incident that ends in January belongs to the next year.
    end = Date.UTC(year + (endMonth < startMonth ? 1 : 0), endMonth, +endDay, +endHour, +endMin);
  } else {
    end = Date.UTC(year, startMonth, +startDay, +endHour, +endMin);
    if (end < start) end += 24 * 60 * 60 * 1000; // rolled past midnight
  }
  return { start, end, ongoing: false };
}

async function getJSON(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url} -> HTTP ${response.status}`);
  return response.json();
}

/**
 * @returns {Promise<{intervals: Array<{start:number,end:number|null,ongoing:boolean}>, incidentCount:number}>}
 */
export async function fetchOutageIntervals() {
  const intervals = [];
  const seen = new Set();

  // Recent incidents first: exact timestamps win over the parsed display strings.
  const recent = await getJSON(`${STATUS_PAGE}/api/v2/incidents.json`);
  for (const incident of recent.incidents || []) {
    seen.add(incident.id);
    if (incident.impact === 'none') continue;
    const start = Date.parse(incident.started_at || incident.created_at);
    const end = incident.resolved_at ? Date.parse(incident.resolved_at) : null;
    if (Number.isNaN(start)) continue;
    intervals.push({ start, end, ongoing: end === null });
  }

  // Pages 1 and 2 cover six months, which is enough to reach back past March 2.
  const pages = await Promise.all([1, 2].map((page) => getJSON(`${STATUS_PAGE}/history.json?page=${page}`)));
  for (const page of pages) {
    for (const month of page.months || []) {
      for (const incident of month.incidents || []) {
        if (seen.has(incident.code)) continue;
        seen.add(incident.code);
        if (incident.impact === 'none') continue;
        const parsed = parseHistoryTimestamp(incident.timestamp, month.year);
        if (parsed) intervals.push(parsed);
      }
    }
  }

  const inWindow = intervals.filter((i) => i.ongoing || i.end > SINCE);
  return { intervals: inWindow, incidentCount: inWindow.length };
}

/**
 * Total wall-clock hours covered by at least one incident since SINCE.
 * Overlapping incidents are merged - two things breaking at once is still
 * only one hour of GitHub being down.
 */
export function outageHours(intervals, now) {
  const spans = intervals
    .map((i) => ({ start: Math.max(i.start, SINCE), end: Math.min(i.ongoing ? now : i.end, now) }))
    .filter((s) => s.end > s.start)
    .sort((a, b) => a.start - b.start);

  let total = 0;
  let current = null;
  for (const span of spans) {
    if (current && span.start <= current.end) {
      current.end = Math.max(current.end, span.end);
    } else {
      if (current) total += current.end - current.start;
      current = { ...span };
    }
  }
  if (current) total += current.end - current.start;

  return total / (1000 * 60 * 60);
}
